import { isAxiosError } from "axios";

import { MAX_CHAT_ATTACHMENT_FILE_SIZE_BYTES } from "../constants/chat";
import {
  EUploadFailureReason,
  type IClassifyUploadFailureProps,
  type ICreateUploadFailureErrorProps,
  type IDescribeRequestErrorDetailsProps,
  type IResolveUploadFailureDisplayProps,
  type IUploadFailureDetails,
  type IUploadFailureError,
  type IUploadFailureI18nParams,
  type TAttachmentDownloadErrorKey,
  type TUploadFailureI18nKey,
} from "../types/uploads";

// NOTE: NSAI-4883 — the three ways an S3 PUT can die used to reach the user as
// one sentence ("Failed to upload file"), because they were plain `Error`s and
// the only reader on the way up understood axios errors. A 404, a dropped
// connection and a 60-second stall then looked identical, which is what made
// several findings in that hygiene wave hard to diagnose.
//
// The reason therefore travels as a tag on the error. Deliberately NOT by
// matching the message text: that string is English, technical, and written for
// the log — a UI that greps it would break on the first reword, and could never
// translate it. Shaped after `createAbortError` in this folder, for the same
// isomorphic reason: no `DOMException`, no custom class, just a `name` and two
// own properties that survive Hermes and the browser alike.
const UPLOAD_FAILURE_ERROR_NAME = "UploadFailureError";

const UPLOAD_FAILURE_I18N_KEYS: Record<
  EUploadFailureReason,
  TUploadFailureI18nKey
> = {
  [EUploadFailureReason.REJECTED]: "common:upload_error.rejected",
  [EUploadFailureReason.NETWORK]: "common:upload_error.network",
  [EUploadFailureReason.STALLED]: "common:upload_error.stalled",
  [EUploadFailureReason.TOO_LARGE]: "common:upload_error.too_large",
  [EUploadFailureReason.UNSUPPORTED_TYPE]:
    "common:upload_error.unsupported_type",
  [EUploadFailureReason.NOT_PERMITTED]: "common:upload_error.not_permitted",
  [EUploadFailureReason.UNKNOWN]: "common:upload_error.unknown",
};

const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;
const HTTP_PAYLOAD_TOO_LARGE = 413;
const HTTP_UNSUPPORTED_MEDIA_TYPE = 415;
const HTTP_BAD_REQUEST = 400;
const HTTP_SERVER_ERROR_MIN = 500;

export const createUploadFailureError = ({
  reason,
  message,
  params,
}: ICreateUploadFailureErrorProps): IUploadFailureError => {
  const error = new Error(message);
  error.name = UPLOAD_FAILURE_ERROR_NAME;

  return Object.assign(error, {
    uploadFailure: reason,
    uploadFailureParams: params ?? {},
  });
};

const isUploadFailureError = (error: unknown): error is IUploadFailureError =>
  error instanceof Error &&
  error.name === UPLOAD_FAILURE_ERROR_NAME &&
  "uploadFailure" in error;

// NOTE: The cross-platform way to ask "why did this upload fail, in words the
// user can read?". Returns null for anything that is not one of our tagged
// upload failures — a server rejection of the presign call, say — so the caller
// keeps its existing precedence instead of this swallowing it.
export const getUploadFailureDetails = (
  error: unknown,
): IUploadFailureDetails | null => {
  if (!isUploadFailureError(error)) return null;

  return {
    reason: error.uploadFailure,
    i18nKey: UPLOAD_FAILURE_I18N_KEYS[error.uploadFailure],
    i18nParams: error.uploadFailureParams,
  };
};

// NOTE: NSAI-4583 — the presign call is plain axios, so its refusal carries no
// tag and its reason has to come from the status. A 403 is a permission
// problem, not a file problem: the assistant presign is gated on
// *_ASSISTANT_WRITE, and telling that user to attach a different file sends
// them in circles. Anything else in the 4xx range collapses to REJECTED rather
// than being guessed at — the presign DTO answers a bare 400 for both an
// oversized `fileSizeBytes` and a disallowed `contentType`.
const classifyUploadHttpStatus = (status: number): EUploadFailureReason => {
  if (status === HTTP_PAYLOAD_TOO_LARGE) {
    return EUploadFailureReason.TOO_LARGE;
  }

  if (status === HTTP_UNSUPPORTED_MEDIA_TYPE) {
    return EUploadFailureReason.UNSUPPORTED_TYPE;
  }

  // NOTE: A 401 is NOT the permission case. The shared api client refreshes the
  // session on 401 and only lets the rejection through once that refresh failed
  // (see `api/client.ts`), so what reaches here is an expired session on its way
  // to the sign-in redirect. Telling that user to ask a project admin for access
  // sends them after the wrong fix, so it falls back to the generic copy.
  if (status === HTTP_UNAUTHORIZED) {
    return EUploadFailureReason.UNKNOWN;
  }

  if (status === HTTP_FORBIDDEN) {
    return EUploadFailureReason.NOT_PERMITTED;
  }

  if (status >= HTTP_BAD_REQUEST && status < HTTP_SERVER_ERROR_MIN) {
    return EUploadFailureReason.REJECTED;
  }

  return EUploadFailureReason.UNKNOWN;
};

interface IBuildDetailsProps {
  reason: EUploadFailureReason;
  params?: IUploadFailureI18nParams;
}

const buildDetails = ({
  reason,
  params,
}: IBuildDetailsProps): IUploadFailureDetails => ({
  reason,
  i18nKey: UPLOAD_FAILURE_I18N_KEYS[reason],
  i18nParams: params ?? {},
});

// NOTE: Turns any upload rejection into something sayable. A tagged pipeline
// failure already knows its reason (and carries the status / seconds the copy
// interpolates), so that path wins; everything else is derived here.
export const classifyUploadFailure = ({
  error,
  fileSizeBytes,
  maxFileSizeBytes = MAX_CHAT_ATTACHMENT_FILE_SIZE_BYTES,
}: IClassifyUploadFailureProps): IUploadFailureDetails => {
  // NOTE: The client can see an oversized file whatever the server said, so this
  // outranks the status mapping. It is the signal that explains the integration
  // path, whose files reach the upload without ever passing a size guard.
  if (fileSizeBytes !== undefined && fileSizeBytes > maxFileSizeBytes) {
    return buildDetails({ reason: EUploadFailureReason.TOO_LARGE });
  }

  const tagged = getUploadFailureDetails(error);
  if (tagged) return tagged;

  if (isAxiosError(error)) {
    // NOTE: No response at all means the request never completed — offline, DNS,
    // CORS or a timeout. Connectivity, not a rejection.
    if (!error.response) {
      return buildDetails({ reason: EUploadFailureReason.NETWORK });
    }

    return buildDetails({
      reason: classifyUploadHttpStatus(error.response.status),
      params: { status: error.response.status },
    });
  }

  return buildDetails({ reason: EUploadFailureReason.UNKNOWN });
};

// NOTE: NSAI-4583 — an integration download dies before the upload pipeline is
// ever reached, so it has its own copy. The status is the part of that failure
// which is stable and actionable; the body's `message` is not, and echoing it is
// what surfaced raw backend strings to the user. Unmapped statuses return null
// so each client falls back to its own generic line.
interface IResolveAttachmentDownloadErrorKeyByStatusProps {
  status: number | undefined;
}

const ATTACHMENT_DOWNLOAD_ERROR_KEY_BY_STATUS: Record<
  number,
  TAttachmentDownloadErrorKey | undefined
> = {
  401: "attachments.file_access_denied",
  403: "attachments.file_access_denied",
  404: "attachments.file_access_not_found",
  410: "attachments.file_access_not_found",
  413: "attachments.file_access_too_large",
};

export const resolveAttachmentDownloadErrorKey = (
  error: unknown,
): TAttachmentDownloadErrorKey | null => {
  if (!isAxiosError(error) || !error.response) return null;

  return resolveAttachmentDownloadErrorKeyByStatus({
    status: error.response.status,
  });
};

// NOTE: NSAI-5230 — the same mapping for a client that has the status but not an
// axios error to read it from. Mobile's integration download is streamed to disk
// by React Native's native downloader, which reports a refusal as a plain
// `Error` (the status is recovered from its message in
// `mobile/src/utils/integration-download.ts`), and an unknown status has to fall
// back to the caller's generic line exactly as an unmapped one does.
export const resolveAttachmentDownloadErrorKeyByStatus = ({
  status,
}: IResolveAttachmentDownloadErrorKeyByStatusProps): TAttachmentDownloadErrorKey | null => {
  if (status === undefined) return null;

  return ATTACHMENT_DOWNLOAD_ERROR_KEY_BY_STATUS[status] ?? null;
};

// NOTE: Collapses a batch of per-file failures into the one message a toast can
// carry. A shared reason is reported as itself; a mixed batch falls back to the
// generic line rather than telling the user one file's story about all of them.
export const resolveUploadFailureDisplay = ({
  failures,
}: IResolveUploadFailureDisplayProps): IUploadFailureDetails => {
  const [first] = failures;

  if (!first || failures.some((failure) => failure.reason !== first.reason)) {
    return buildDetails({ reason: EUploadFailureReason.UNKNOWN });
  }

  return first;
};

// NOTE: NSAI-4880 — the technical half of a failure, for a "View details"
// disclosure next to the sentence the user reads. Deliberately not the log
// line: that one carries the resolved request URL, which on a card is noise
// the reader cannot act on. What survives is what a support conversation
// actually needs — the status the server answered with, or the axios code
// proving no response ever arrived. Returns null when there is nothing to add
// beyond what the card already says.
//
// NSAI-5732 lifted this out of the mobile app so the web probe-upload card
// renders the same two-part failure; the "no response" wording stays a caller
// argument because each app translates through its own i18n instance.
export const describeRequestErrorDetails = ({
  error,
  noResponseText,
}: IDescribeRequestErrorDetailsProps): string | null => {
  if (isAxiosError(error)) {
    const status = error.response?.status;
    const parts = [
      status !== undefined ? `HTTP ${status}` : noResponseText,
      error.code,
      error.message,
    ].filter((part): part is string => !!part);

    return parts.join(" · ");
  }

  if (error instanceof Error) return error.message;

  return null;
};
